// o4ParseJobData.js
//
// This module is a direct copy of parseJobData.js, renamed for the
// o4‑mini configuration.  It parses job description data returned
// from the back end and normalises it into a consistent shape for
// consumption by the UI.  Both private‑sector and legacy federal
// formats are supported.  See parseJobData.js for additional
// context.

function parseJobData(data) {
    // This parser supports both the legacy federal format and the new private‑sector format.

    // Initialize fields with sensible defaults
    let specializedExperience = '';
    let experienceRequirements = '';
    let competencies = [];
    let softSkills = [];
    let knowledge = [];
    let requiredSkills = [];
    let preferredSkills = [];
    let educationRequirements = '';
    let jobSummary = '';
    let keywords = [];

    /*
     * Determine if the incoming data uses the new private‑sector format. We treat it as
     * private‑sector when any of the following are present:
     *   - experienceRequirements
     *   - requiredSkills
     *   - softSkills
     *   - preferredSkills
     *   - educationRequirements
     *   - jobSummary
     *   - keywords
     *   - Competencies as an array
     *   - Knowledge as an array
     *   The old federal parser expects nested objects with pipe‑delimited strings. By
     *   explicitly checking for these modern fields, we avoid misinterpreting the
     *   response structure.
     */
    const isPrivateFormat =
        data.experienceRequirements !== undefined ||
        data.requiredSkills !== undefined ||
        data.softSkills !== undefined ||
        data.preferredSkills !== undefined ||
        data.educationRequirements !== undefined ||
        data.jobSummary !== undefined ||
        data.keywords !== undefined ||
        Array.isArray(data.Competencies) ||
        Array.isArray(data.Knowledge);

    if (isPrivateFormat) {
        // Experience requirements
        if (typeof data.experienceRequirements === 'string') {
            experienceRequirements = data.experienceRequirements;
            specializedExperience = data.experienceRequirements;
        } else if (typeof data.SpecializedExperience === 'string') {
            // Fallback: some APIs may mirror this
            experienceRequirements = data.SpecializedExperience;
            specializedExperience = data.SpecializedExperience;
        }

        // Required skills can be an array of strings or objects
        if (Array.isArray(data.requiredSkills)) {
            requiredSkills = data.requiredSkills.map((item) => {
                if (typeof item === 'string') {
                    return item.trim();
                }
                // If object, attempt to map known fields
                const name = item.name || item.primary || item.skill || '';
                const description = item.description || '';
                // Preserve object structure when a description exists, otherwise just return name
                return description
                    ? { name: name.trim(), description: description.trim() }
                    : name.trim();
            });
            // Mirror the required skills into knowledge for legacy compatibility
            knowledge = Array.isArray(requiredSkills)
                ? requiredSkills.map((item) => {
                      if (typeof item === 'string') return item;
                      // Convert object into unified structure { name, description }
                      return { name: item.name || item.primary || item.skill || '', description: item.description || '' };
                  })
                : [];
        } else if (Array.isArray(data.Knowledge)) {
            // Some backends may deliver knowledge array instead of requiredSkills
            requiredSkills = data.Knowledge.map((item) => {
                if (typeof item === 'string') return item.trim();
                const name = item.name || item.primary || item.skill || '';
                const description = item.description || '';
                return description
                    ? { name: name.trim(), description: description.trim() }
                    : name.trim();
            });
            knowledge = requiredSkills.map((item) => {
                if (typeof item === 'string') return item;
                return { name: item.name || item.primary || item.skill || '', description: item.description || '' };
            });
        }

        // Preferred skills (string or object array)
        if (Array.isArray(data.preferredSkills)) {
            preferredSkills = data.preferredSkills.map((item) => {
                if (typeof item === 'string') return item.trim();
                const name = item.name || item.primary || item.skill || '';
                const description = item.description || '';
                return description
                    ? { name: name.trim(), description: description.trim() }
                    : name.trim();
            });
        }

        // Soft skills or competencies (string or object array)
        if (Array.isArray(data.softSkills)) {
            softSkills = data.softSkills.map((item) => {
                if (typeof item === 'string') {
                    return { primary: item.trim(), description: '' };
                }
                // For objects, map primary/name, subcategory, description
                const primary = item.primary || item.name || '';
                const subcategory = item.subcategory || '';
                const description = item.description || '';
                return subcategory
                    ? { primary: primary.trim(), subcategory: subcategory.trim(), description: description.trim() }
                    : { primary: primary.trim(), description: description.trim() };
            });
            competencies = softSkills;
        } else if (Array.isArray(data.Competencies)) {
            // Some backends may deliver competencies array (mirrored for legacy)
            softSkills = data.Competencies.map((item) => {
                if (typeof item === 'string') {
                    return { primary: item.trim(), description: '' };
                }
                const primary = item.primary || item.name || '';
                const subcategory = item.subcategory || '';
                const description = item.description || '';
                return subcategory
                    ? { primary: primary.trim(), subcategory: subcategory.trim(), description: description.trim() }
                    : { primary: primary.trim(), description: description.trim() };
            });
            competencies = softSkills;
        }

        // Education requirements (string)
        if (typeof data.educationRequirements === 'string') {
            educationRequirements = data.educationRequirements;
        }
        // Job summary (string)
        if (typeof data.jobSummary === 'string') {
            jobSummary = data.jobSummary;
        }

        // Keywords (string array). Use legacyKeywords if provided for backward compatibility
        if (Array.isArray(data.keywords)) {
            keywords = data.keywords.map((item) => (typeof item === 'string' ? item.trim() : item));
        } else if (Array.isArray(data.legacyKeywords)) {
            keywords = data.legacyKeywords.map((item) => (typeof item === 'string' ? item.trim() : item));
        }
    }

    /*
     * Legacy federal format: nested objects with string values. This code path is invoked
     * when we did not detect the private‑sector fields above (isPrivateFormat === false).
     */
    if (!isPrivateFormat) {
        // data.SpecializedExperience may be an object mapping "SPECIALIZED EXPERIENCE" to a string or a string directly
        if (!experienceRequirements && data.SpecializedExperience) {
            if (typeof data.SpecializedExperience === 'object') {
                specializedExperience = data.SpecializedExperience['SPECIALIZED EXPERIENCE'] || '';
                experienceRequirements = specializedExperience;
            } else if (typeof data.SpecializedExperience === 'string') {
                specializedExperience = data.SpecializedExperience;
                experienceRequirements = specializedExperience;
            }
        }
        // Parse competencies from pipe‑delimited string
        if (!competencies.length && data.Competencies && data.Competencies.Competencies) {
            const competenciesString = data.Competencies.Competencies;
            competencies = competenciesString.split('|').map((item) => {
                const parts = item.split(':');
                if (parts.length === 3) {
                    const [primary, subcategory, description] = parts;
                    return {
                        primary: primary.trim(),
                        subcategory: subcategory.trim(),
                        description: description.trim(),
                    };
                } else if (parts.length === 2) {
                    const [name, description] = parts;
                    return {
                        name: name.trim(),
                        description: description.trim(),
                    };
                } else {
                    return { name: parts[0].trim(), description: '' };
                }
            });
        }
        // Parse knowledge from pipe‑delimited string
        if (!knowledge.length && data.Knowledge && data.Knowledge.Knowledge) {
            const knowledgeString = data.Knowledge.Knowledge;
            if (knowledgeString) {
                knowledge = knowledgeString.split('|').map((item) => {
                    const [name, description] = item.split(':');
                    return { name: name.trim(), description: description ? description.trim() : '' };
                });
            }
        }
        // Parse keywords from pipe‑delimited string
        if (!keywords.length && data.Keywords && data.Keywords.Keywords) {
            const keywordsString = data.Keywords.Keywords;
            if (keywordsString) {
                keywords = keywordsString.split('|').map((item) => item.trim());
            }
        }
    }

    // Standardise outputs
    return {
        specializedExperience,
        experienceRequirements,
        competencies,
        softSkills,
        knowledge,
        requiredSkills,
        preferredSkills,
        educationRequirements,
        jobSummary,
        keywords,
    };
}

export default parseJobData;